import './App.css';
import { useState, Fragment, useEffect, useRef } from 'react';
import Liquid from '@liquidcloud/sdk';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  ShoppingCartIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import Loading from './components/Loading/Loading';

const liquid = await Liquid({
  clientId: '8b6f78dfae88b321b870ec9e42734bf688703f7683c1c8c6affd72ce',
  env: 'production',
});

function App() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [shipAddress, setShipAddress] = useState('');
  const [open, setOpen] = useState(false);
  const [autoCompleteResults, setAutoCompleteResults] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [availability, setAvailability] = useState(null);
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  const [showRetailers, setShowRetailers] = useState(false);
  const [showProducts, setShowProducts] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cartQty, setCartQty] = useState([]);
  const productRef = useRef(null);
  // get data passed from WP to app
  const appDiv = document.getElementById("root");
  const passedProps = JSON.parse(appDiv.dataset.props);

  useEffect(() => {
    const loadStorageData = async () => {
      // Retrieve cart from local storage
      const storedCart = JSON.parse(localStorage.getItem('cart'));

      if (storedCart === null || storedCart.length === 0) {
        // If no cart is found in local storage, create a new one
        const liquidCart = await liquid.cart({
          cartItems: [],
          giftOrder: false,
        });
        setCart(liquidCart);
      } else {
        // If a cart is found in local storage, set it in the state
        setCart(storedCart);
      }

      // Retrieve address from local storage
      if (!selectedAddress) {
        const savedAddress = JSON.parse(localStorage.getItem('address'));
        setSelectedAddress(savedAddress);
      }
    };

    loadStorageData();
    //prepopulate products without setting an address so retailers don't show
    fetchProducts('20 West 40th Street, New York, NY, USA');
  }, []);

  useEffect(() => {
    // save cart to local storage
    if (cart.length === 0) return;
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const fetchProducts = async (currentAddress) => {
    const products = await liquid.product({
      ids: ['GROUPING-528477', 'GROUPING-1656069', 'GROUPING-1656068','GROUPING-1876111', 'GROUPING-2261151'],
      shipAddress: currentAddress,
      top100: false,
    });
    console.log('prods', products);
    setProducts(products);
  };

  const updateCart = async () => {
    const retailer = selectedRetailer;
    const inCart = cart.cartItems.find(
      (o) => o.product.id === retailer?.variantId
    );
    let qty = selectedQuantity;

    // if already in the cart incrament the quantity instead of resettign to 1
    if (inCart) {
      const index = cart.cartItems.findIndex(
        (o) => o.product.id === retailer?.variantId
      );

      if (index !== -1) {
        qty = cart.cartItems[index].quantity + selectedQuantity;
      }
      setCartQty(qty);
    }
    if (qty > retailer.inStock) {
      setErrorMessage(
        `Retailer has only ${retailer.inStock} available in stock`
      );
      return;
    }

    const newCart = {
      id: cart.id,
      cartItems: [
        ...cart.cartItems,
        ...[
          {
            variantId: retailer?.variantId,
            quantity: qty,
          },
        ],
      ],
    };

    try {
      const result = await liquid.cart(newCart);
      setCart(result);
      setOpen(true);
      setShowProducts(false);
      setShowRetailers(true);
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage('Failed to add product to cart. Please try again later.');
      // log error
    }
  };

  const checkout = async () => {
    try {
      setLoading(true);
      if (!selectedAddress?.address1.trim()) {
        alert('Please enter a valid delivery address');
      }
      /// Create checkout url
      const checkoutUrl = await liquid.checkout({
        cartId: cart.id,
        address: {
          address1: selectedAddress.address1,
          city: selectedAddress.city,
          state: selectedAddress.state,
          zipCode: selectedAddress.zipCode,
        },
      });

      if (checkoutUrl.url) {
        // Clear cart and address from local storage
        localStorage.removeItem('cart');
        localStorage.removeItem('address');
        // Redirect to checkout
        window.location = checkoutUrl.url;
      }
    } catch (error) {
      // log error
      setLoading(false);
    } finally {
    }
  };

  const getAddress = async (event) => {
    const inputAddress = event.target.value;
    setShipAddress(inputAddress);

    /// If the input is empty, clear the autocomplete results
    if (inputAddress.trim().length === 0) {
      setAutoCompleteResults([]);
      return;
    }

    /// Fetch autocomplete results
    try {
      const predictions = await liquid.address({
        search: inputAddress,
      });
      setAutoCompleteResults(predictions);
    } catch (error) {
      //log error
    }
  };

  const handleAddressClick = async (currentAddress) => {
    /// Clear autocomplete results
    setAutoCompleteResults([]);

    /// Fetch products for the selected address
    await fetchProducts(currentAddress.description);
    setShipAddress(currentAddress.description);

    try {
      /// Fetch the full address details
      const placeDetail = await liquid.address({
        placeId: currentAddress.placeId,
      });
      setSelectedAddress(placeDetail);
      localStorage.setItem('address', JSON.stringify(placeDetail));

      /// clear cart
      const newCart = await liquid.cart({
        cartItems: [],
        giftOrder: false,
      });
      setCart(newCart);
      setShowRetailers(true);
      setShowProducts(false);
      setErrorMessage(null);
      productRef.current?.scrollIntoView({block: "start", inline: "nearest"});
    } catch (error) {
      //log error
    }
  };

  const handleCart = () => {
    setOpen(!open);
  };

  const handleQuantityChange = (event) => {
    setSelectedQuantity(Number(event.target.value));
  };

  const handleRetailerClick = (retailer, availability) => {
    setSelectedRetailer(retailer, availability);
    setAvailability(availability);
    setShowProducts(true);
    setShowRetailers(false);
    setSelectedQuantity(1);
  };

  const handleRemoveFromCart = async (productId) => {
    if (!productId) return;
    const updatedCart = {
      id: cart.id,
      cartItems: [
        ...cart.cartItems,
        ...[
          {
            variantId: productId,
            quantity: 0,
          },
        ],
      ],
    };

    try {
      const result = await liquid.cart(updatedCart);
      setCart(result);
    } catch (error) {
      // log error
    }
  };

  const handleCartQtyChange = async (event, item) => {
    const qty = Number(event.target.value);
    //setCartQty(qty);
    setCartQty((prevCartQty) => ({
      ...prevCartQty,
      [item.product.id]: qty,
    }));
    const updatedCart = {
      id: cart.id,
      cartItems: [
        ...cart.cartItems,
        ...[
          {
            variantId: item.product.id,
            quantity: qty,
          },
        ],
      ],
    };

    try {
      const result = await liquid.cart(updatedCart);
      setCart(result);
    } catch (error) {
      // log error
    }
  };

  return (
    <div className='app flex gap-8 flex-col'>
      <div className='h-6 w-6 fixed z-[999999] md:z-[999] top-2 md:top-5  right-4 md:right-10'>
      <ShoppingCartIcon
        onClick={handleCart}
        className='h-8 w-8 text-white md:text-[#27588C] cursor-pointer relative z-[10]'
        aria-hidden='true'
        />
        {cart.itemCount > 0 && (
          <span className='absolute -top-[5px] -right-[16px] fr-bg text-white font-bold h-4 w-4 text-center rounded-full text-[10px] flex justify-center'>{cart.itemCount}</span>
        )}
      </div>
      <div className="bg-no-repeat bg-cover bg-left md:bg-center py-8 px-4 md:px-24 md:py-12 flex justify-center items-center w-full flex-col-reverse  md:flex-row" style={{ backgroundImage: `url( '${passedProps.heroBackground} ')` }}>
          <div className='w-full md:w-7/12 text-center'>
                    <h1 className="text-5xl md:text-7xl w-full mb-8 text-bold fb-color app-title uppercase">Buy Fishers <br className='hidden md:block'/> Island Lemonade</h1>
        <form className='w-full relative' autoComplete='off'>
        <label
          htmlFor='searchAddress'
          className='block text-lg md:text-xl font-bold leading-6 fb-color mb-2 text-center'
        >
          Where would you like Fishers Island Lemonade Delivered?
        </label>
        <input
          type='text'
          name='searchAddress'
          placeholder='123 Main Street...'
          className='w-full py-1.5 px-1.5 fb-color text-md fr-border placeholder:text-gray-400  md:text-sm md:leading-6'
          onChange={getAddress}
          value={shipAddress}
        />

        {autoCompleteResults.length > 0 && (
          <ul className='w-full py-1.5  fb-border bt-none fb-color bg-white absolute'>
            {autoCompleteResults.map((result, i) => (
              <li
                key={result.placeId || i}
                className='pr-1.5 pl-3 text-sm md:text-lg hover:bg-[#27588C] hover:text-white my-0.25 py-1 transition-all duration-300'
              >
                <button
                  className='flex gap-2'
                  type='button'
                  onClick={() => handleAddressClick(result)}
                >
                  <MapPinIcon className='h-6 w-6 fr-color' />
                  {result.description}
                </button>
              </li>
            ))}
          </ul>
        )}
      </form>
        </div>
        <div className="w-full md:w-5/12 mb-12 md:mb-0">
          <img  src={ passedProps.heroImage} alt="fishers island lemonade"/>
        </div>
      </div>


      <div ref={ productRef} className='product-grid mt-8 w-full max-w-screen-xl mx-auto'>
          {products ? (
            <>
                {!showProducts && products.some((product) =>
                  product
                ) && (
                  <div className='w-full flex flex-wrap gap-16 justify-center'>
                    {products.map((product) => (
                      <div key={product.id} className='w-full grid-item md:w-[calc(33%-4rem)] '>
                        <h2 className='text-xl text-center fr-color font-bold mb-2 md:h-14 product-title'>{product.name}</h2>
                        
                        <div className='mb-2 bg-img w-full h-80 bg-no-repeat bg-contain bg-center' style={{ backgroundImage: `url( '${product?.images[0].replace(/^["'](.+(?=["']$))["']$/, '$1')} ')` }}></div>
                        <div className="text-sm text-center fb-color mb-6 product-description min-h-0 md:min-h-[120px]" dangerouslySetInnerHTML={{ __html: product.descriptionHtml }} />
              
                        {showRetailers && product.variants.map((variant) => {
                          return variant.retailers
                            .map((retailer) => (
                              <div
                                key={retailer.variantId}
                                onClick={() => {
                                  retailer['productImages'] = variant.images;
                                  retailer['productName'] = product.name;
                                  handleRetailerClick(retailer, retailer.type);
                                }}
                                className={`border-t-2  fb-borderColor cursor-pointer ${retailer.shippingMethod.deliveryExpectation === 'On backorder'? 'disabled' : ''}`}
                              >
                                <div className='my-2 py-2 px-2 mx-1  rounded-md hover:bg-[#fef182] transition-all duration-300 text-left'>
                                  <p className='text-xs fr-color uppercase italic text-left'>{retailer.type}</p>
                                  <h3 className='text-lg font-bold fb-color text-left'>{retailer.retailer.name}</h3>
                                  
                                  <div className='flex justify-between items-center'>
                                  <p className='text-xs fb-color'>
                                    {retailer.shippingMethod.deliveryExpectation}
                                  </p>
                                  <p className='font-bold text-sm fr-color'>${retailer.price}</p>
                                  </div>
                
                                </div>
 
                              </div>
                            ))
                        })}
                        { shipAddress && product.variants.length <= 0 && (<div className='h-24 border-t-2  fb-borderColor' ><h3 className='text-lg font-bold fr-color my-2 py-2 px-2 mx-1 text-center'>Product Not Available <br/> in your Area</h3> </div>)}
                      </div>
                    ))}
                  </div>
                )}
            </>
          ) : (
            <div>No Products Available in Your Area</div>
        )}
              <div>
        {showProducts && (
          <div className='w-full ml-auto mr-auto mt-4 block text-left'>
              <div className='flex flex-wrap items-center justify-center gap-4'>
                            <button
              className='fb-color font-bold w-full text-left'
              type='button'
              onClick={() => {
                setShowProducts(false);
                setShowRetailers(true);
                setErrorMessage(null);
              }}
            >
              {'<< Back'}
            </button>
                                        <img
              src={selectedRetailer.productImages[0]}
              alt={selectedRetailer.productName}
              className='product-image w-1/4'
            />
              <div>
              <h1 className='text-2xl font-bold mb-2 fb-color border-b-2 fr-borderColor'>{selectedRetailer.productName}</h1>
                <h2 className='text-lg font-bold fb-color mb-2  text-left'> {availability} By {selectedRetailer.retailer.name}</h2>
                <div className='flex justify-between w-full mb-4'>
                  <p className='text-xs fb-color uppercase italic'>{selectedRetailer.shippingMethod.deliveryExpectation}</p>
                  <p className='font-bold text-sm fb-color'>${parseFloat(selectedRetailer.price).toFixed(2)}</p>
                </div>
                <div className='flex flex-wrap justify-between items-end mb-6'>
                <p className='text-xl font-bold fb-color mb-2 w-full text-left'>Quantity</p>{' '}
                  <select onChange={handleQuantityChange} value={selectedQuantity} className='border-2 fr-borderColor p-2 appearance-none text-center'>
                    {Array.from(
                      { length: Number(selectedRetailer.inStock) },
                      (_, index) => index + 1
                    ).map((quantity) => (
                      <option key={quantity} value={quantity}>
                        {quantity}
                      </option>
                    ))}
                  </select>
                  <></>
                  <p className='fr-color'>{errorMessage}</p>
                  <p className='font-bold text-lg fr-color'>
                    Total: $
                    {(parseFloat(selectedRetailer.price) * selectedQuantity).toFixed(
                      2
                    )}
                  </p>
              </div>

            <button
              className='px-2 py-1.5 w-full fb-border text-md font-semibold fb-bg text-white hover:bg-[transparent] hover:text-[#27588C] transition-all duration-300'
                  onClick={() => {
                    updateCart();
                  }}
            >
              Add To Cart
            </button>
              </div>
              </div>
              </div>
        )}
      </div>
        </div>
  
      {/* Cart Drawer */}
      <Transition.Root show={open} as={Fragment}>
        <Dialog as='div' className='relative z-[9999999] md:z-[999]' onClose={setOpen}>
          <Transition.Child
            as={Fragment}
            enter='ease-in-out duration-500'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in-out duration-500'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
          </Transition.Child>

          <div className='fixed inset-0 overflow-hidden'>
            <div className='reative  md:z-[9999] inset-0 overflow-hidden'>
              <div className='pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10'>
                <Transition.Child
                  as={Fragment}
                  enter='transform transition ease-in-out duration-500 md:duration-700'
                  enterFrom='translate-x-full'
                  enterTo='translate-x-0'
                  leave='transform transition ease-in-out duration-500 md:duration-700'
                  leaveFrom='translate-x-0'
                  leaveTo='translate-x-full'
                >
                  <Dialog.Panel className='pointer-events-auto w-screen max-w-md'>
                    <div className='flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl'>
                      <div className='px-4 md:px-6'>
                        <div className='flex items-start justify-between'>
                          <Dialog.Title className='text-2xl font-bold mb-2 leading-6 fb-color'>
                            Your Cart
                          </Dialog.Title>
                          <div className='ml-3 flex h-7 items-center'>
                            <button
                              type='button'
                              className='relative rounded-md bg-white text-gray-400 hover:text-gray-500'
                              onClick={() => setOpen(false)}
                            >
                              <span className='absolute -inset-2.5' />
                              <span className='sr-only'>Close panel</span>
                              <XMarkIcon
                                className='h-6 w-6 fb-color'
                                aria-hidden='true'
                              />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className='relative mt-6 flex-1 px-4 md:px-6 z-[9999999] md:z-[9999]'>
                        <div className='cart mb-8'>
                          {cart?.cartItems?.length > 0 ? (
                            cart.cartItems.map((item) => (
                              <div
                                key={item.product.name}
                                className='cart-item  border-t-2 fb-borderColor py-4 '
                              >
                                <div className='p-2 flex flex gap-2 content-center'>
                                {item.product.thumbUrl && (
                                  <img
                                    src={item.product.thumbUrl}
                                      alt={item.product.name}
                                  />
                                )}
                                  <div>
                                    <div className='flex justify-between items-start gap-4'>
                                    <h2 className='text-md font-bold  fb-color'>
                                    {item.productGrouping.name}
                                    </h2>
                                                                                                          <button
                                    className=''
                                    onClick={() =>
                                      handleRemoveFromCart(item.product.id)
                                    }
                                  >
                              <XMarkIcon
                                className='h-4 w-4 fb-color'
                                aria-hidden='true'
                              />
                                  </button>
                                    </div>

                                  <p className='text-xs fr-color font-semibold mb-2'>
                                    {item.product.volume}
                                    </p>
                                  <p className='text-xs fb-color italic'>
                                    Fulfilled by {item.retailerName}
                                  </p>
                                  <p className='text-xs fb-color italic mb-2'>
                                    {item.shortDeliveryExpectation}
                                  </p>
                                  <div className='flex justify-between mb-1'>
                                    <p className='text-base fb-color'>
                                      <select
                                        onChange={(e) =>
                                          handleCartQtyChange(e, item)
                                        }
                                        value={
                                          cartQty[item.product.id] ??
                                          item.quantity
                                        }
                                          className='border-2 fr-borderColor p-1 text-xs appearance-none text-center'
                                      >
                                        {Array.from(
                                          {
                                            length: Number(
                                              item.product.inStock
                                            ),
                                          },
                                          (_, index) => index + 1
                                        ).map((quantity) => (
                                          <option
                                            key={quantity}
                                            value={quantity}
                                          >
                                            {quantity}
                                          </option>
                                        ))}
                                      </select>
                                    </p>
                                    <p className='text-base font-bold fb-color'>
                                      <span>$</span>{item.product.price}
                                    </p>
                                  </div>
                                  </div>
                                  </div>
                              </div>
                            ))
                          ) : (
                            <p>Your Cart Is Currently Empty</p>
                          )}
                        </div>
                        {loading && <Loading />}
                        <div className='border-t-2 fb-borderColor py-2'>
                          {cart?.cartItems?.length > 0 && (
                            <>
                              <div className='flex justify-between mb-4 mt-2'>
                                <p className='font-bold text-md fb-color'>Subtotal ({cart.itemCount} {cart.itemCount > 1 ? 'items' : 'item'})  </p>
                                <p className='text-md fb-color'><span>$</span>{cart.subtotal }</p>
                              </div>
                            <div className='text-xs italic fb-color mb-4 '>
                            By placing this order, I give consent to Fishers Island Lemonade to process and store the personal data on this form in accordance with its <a className="underline text-xs italic" href="/user-agreement">Terms</a> and <a className="underline text-xs italic" href="/privacy-policy">Privacy Policy</a>. The personal data submitted via this form will be retained for transactional order fulfillment communications.
                          </div>
                              <button
                            className='max-w-full px-2 py-1.5  mb-2 w-full fb-border text-md font-semibold fb-bg text-white hover:bg-[transparent] hover:text-[#27588C] transition-all duration-300'
                            onClick={checkout}
                            disabled={loading}
                          >
                            {loading ? 'Loading...' : 'Checkout'}
                          </button>
                          </>
                        )}
                        <button
                          className='max-w-full px-2 py-1.5 w-full  mb-2 fb-border text-md font-semibold fb-color bg-[transparent] hover:bg-[#27588C] hover:text-white transition-all duration-300'
                          onClick={() => setOpen(false)}
                        >
                          Continue Shopping
                          </button>
                          </div>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}

export default App;

